import React, { useState, useEffect } from 'react';
import { 
  FaUser, 
  FaBell, 
  FaLock, 
  FaEdit, 
  FaSave, 
  FaTimes, 
  FaEye,
  FaEyeSlash,
  FaTrash,
  FaInfoCircle
} from 'react-icons/fa';
import { auth, db } from '../../configs/firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { Link } from "react-router-dom"
import { updatePassword, updateEmail, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

const EmployerSettings = () => {
  const [activeTab, setActiveTab] = useState('contact'); // Start with contact instead of company
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState({
    contact: false,
    notifications: false,
    security: false
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Contact Information State
  const [contactData, setContactData] = useState({
    contactPersonName: '',
    contactPersonTitle: '',
    email: '',
    phone: '',
    alternativePhone: ''
  });

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: {
      newApplications: true,
      applicationUpdates: true,
      systemUpdates: true,
      marketingEmails: false
    },
    pushNotifications: {
      newApplications: true,
      applicationUpdates: false,
      reminders: true
    }
  });

  // Security Settings State
  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Load employer data on component mount (only contact and notification data)
  useEffect(() => {
    loadEmployerData();
  }, []);

  const loadEmployerData = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const employerDoc = await getDoc(doc(db, 'employers', user.uid));
        if (employerDoc.exists()) {
          const data = employerDoc.data();
          
          // Load contact data
          setContactData({
            contactPersonName: data.contactPersonName || '',
            contactPersonTitle: data.contactPersonTitle || '',
            email: data.email || user.email || '',
            phone: data.phone || '',
            alternativePhone: data.alternativePhone || ''
          });

          // Load notification settings
          if (data.notificationSettings) {
            setNotificationSettings(data.notificationSettings);
          }
        }
      }
    } catch (error) {
      console.error('Error loading employer data:', error);
    }
  };

  const handleEdit = (section) => {
    setEditing(prev => ({ ...prev, [section]: true }));
  };

  const handleCancel = (section) => {
    setEditing(prev => ({ ...prev, [section]: false }));
    // Reset data to original values
    loadEmployerData();
  };

  const handleSave = async (section) => {
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (user) {
        const employerRef = doc(db, 'employers', user.uid);
        
        switch (section) {
          case 'contact':
            await updateDoc(employerRef, {
              contactPersonName: contactData.contactPersonName,
              contactPersonTitle: contactData.contactPersonTitle,
              email: contactData.email,
              phone: contactData.phone,
              alternativePhone: contactData.alternativePhone,
              updatedAt: new Date()
            });
            break;
          case 'notifications':
            await updateDoc(employerRef, {
              notificationSettings: notificationSettings,
              updatedAt: new Date()
            });
            break;
        }
        
        setEditing(prev => ({ ...prev, [section]: false }));
        alert('Settings updated successfully!');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Error updating settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (securityData.newPassword !== securityData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    if (securityData.newPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, securityData.currentPassword);
      
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, securityData.newPassword);
      
      setSecurityData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      alert('Password updated successfully!');
    } catch (error) {
      console.error('Error updating password:', error);
      alert('Error updating password: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const SectionHeader = ({ title, section, icon: Icon }) => (
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-lg font-semibold text-gray-800 flex items-center">
        <Icon className="mr-2 text-orange-500" />
        {title}
      </h3>
      {section !== 'security' && (
        <div className="flex space-x-2">
          {editing[section] ? (
            <>
              <button
                onClick={() => handleSave(section)}
                disabled={loading}
                className="flex items-center px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
              >
                <FaSave className="mr-1" size={12} />
                Save
              </button>
              <button
                onClick={() => handleCancel(section)}
                disabled={loading}
                className="flex items-center px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                <FaTimes className="mr-1" size={12} />
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => handleEdit(section)}
              className="flex items-center px-3 py-1 bg-orange-500 text-white rounded-md hover:bg-orange-600"
            >
              <FaEdit className="mr-1" size={12} />
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderContactSettings = () => (
    <div className="space-y-8">
      {/* Info box about company profile */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <FaInfoCircle className="text-blue-500 mr-3 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">Company Information</h4>
            <p className="text-blue-700 text-sm mb-2">
              Manage your company details, branding, team, and culture in the dedicated Company Profile section.
            </p>
            <Link to="/employer/company-profile" 
              className="text-blue-600 hover:text-blue-800 font-medium text-sm"
            >
              Go to Company Profile Management →
            </Link>
          </div>
        </div>
      </div>

      <SectionHeader title="Contact Information" section="contact" icon={FaUser} />
      
      {editing.contact ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Person Name *
              </label>
              <input
                type="text"
                value={contactData.contactPersonName}
                onChange={(e) => setContactData(prev => ({ ...prev, contactPersonName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Title *
              </label>
              <input
                type="text"
                value={contactData.contactPersonTitle}
                onChange={(e) => setContactData(prev => ({ ...prev, contactPersonTitle: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={contactData.email}
                onChange={(e) => setContactData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                value={contactData.phone}
                onChange={(e) => setContactData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alternative Phone
              </label>
              <input
                type="tel"
                value={contactData.alternativePhone}
                onChange={(e) => setContactData(prev => ({ ...prev, alternativePhone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
            <div>
              <p className="text-sm text-gray-500">Contact Person</p>
              <p className="font-medium">{contactData.contactPersonName || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Job Title</p>
              <p className="font-medium">{contactData.contactPersonTitle || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium">{contactData.email || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="font-medium">{contactData.phone || 'Not provided'}</p>
            </div>
            {contactData.alternativePhone && (
              <div>
                <p className="text-sm text-gray-500">Alternative Phone</p>
                <p className="font-medium">{contactData.alternativePhone}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-8">
      <SectionHeader title="Notification Preferences" section="notifications" icon={FaBell} />
      
      <div className="space-y-6">
        <div>
          <h4 className="text-md font-medium text-gray-800 mb-4">Email Notifications</h4>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notificationSettings.emailNotifications.newApplications}
                onChange={(e) => setNotificationSettings(prev => ({
                  ...prev,
                  emailNotifications: {
                    ...prev.emailNotifications,
                    newApplications: e.target.checked
                  }
                }))}
                className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <span className="ml-2 text-sm text-gray-700">New applications received</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notificationSettings.emailNotifications.applicationUpdates}
                onChange={(e) => setNotificationSettings(prev => ({
                  ...prev,
                  emailNotifications: {
                    ...prev.emailNotifications,
                    applicationUpdates: e.target.checked
                  }
                }))}
                className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <span className="ml-2 text-sm text-gray-700">Application status updates</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notificationSettings.emailNotifications.systemUpdates}
                onChange={(e) => setNotificationSettings(prev => ({
                  ...prev,
                  emailNotifications: {
                    ...prev.emailNotifications,
                    systemUpdates: e.target.checked
                  }
                }))}
                className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <span className="ml-2 text-sm text-gray-700">System updates and maintenance</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notificationSettings.emailNotifications.marketingEmails}
                onChange={(e) => setNotificationSettings(prev => ({
                  ...prev,
                  emailNotifications: {
                    ...prev.emailNotifications,
                    marketingEmails: e.target.checked
                  }
                }))}
                className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <span className="ml-2 text-sm text-gray-700">Marketing emails and tips</span>
            </label>
          </div>
        </div>

        <div>
          <h4 className="text-md font-medium text-gray-800 mb-4">Push Notifications</h4>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notificationSettings.pushNotifications.newApplications}
                onChange={(e) => setNotificationSettings(prev => ({
                  ...prev,
                  pushNotifications: {
                    ...prev.pushNotifications,
                    newApplications: e.target.checked
                  }
                }))}
                className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <span className="ml-2 text-sm text-gray-700">New applications</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notificationSettings.pushNotifications.applicationUpdates}
                onChange={(e) => setNotificationSettings(prev => ({
                  ...prev,
                  pushNotifications: {
                    ...prev.pushNotifications,
                    applicationUpdates: e.target.checked
                  }
                }))}
                className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <span className="ml-2 text-sm text-gray-700">Application updates</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notificationSettings.pushNotifications.reminders}
                onChange={(e) => setNotificationSettings(prev => ({
                  ...prev,
                  pushNotifications: {
                    ...prev.pushNotifications,
                    reminders: e.target.checked
                  }
                }))}
                className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <span className="ml-2 text-sm text-gray-700">Reminders and deadlines</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-8">
      <SectionHeader title="Security Settings" section="security" icon={FaLock} />
      
      {/* Password Update */}
      <div>
        <h4 className="text-md font-medium text-gray-800 mb-4">Change Password</h4>
        <div className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showPassword.current ? "text" : "password"}
                value={securityData.currentPassword}
                onChange={(e) => setSecurityData(prev => ({ ...prev, currentPassword: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => ({ ...prev, current: !prev.current }))}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword.current ? <FaEyeSlash className="text-gray-400" /> : <FaEye className="text-gray-400" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword.new ? "text" : "password"}
                value={securityData.newPassword}
                onChange={(e) => setSecurityData(prev => ({ ...prev, newPassword: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => ({ ...prev, new: !prev.new }))}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword.new ? <FaEyeSlash className="text-gray-400" /> : <FaEye className="text-gray-400" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showPassword.confirm ? "text" : "password"}
                value={securityData.confirmPassword}
                onChange={(e) => setSecurityData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => ({ ...prev, confirm: !prev.confirm }))}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword.confirm ? <FaEyeSlash className="text-gray-400" /> : <FaEye className="text-gray-400" />}
              </button>
            </div>
          </div>

          <button
            onClick={handlePasswordUpdate}
            disabled={loading || !securityData.currentPassword || !securityData.newPassword || !securityData.confirmPassword}
            className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                Updating Password...
              </>
            ) : (
              'Update Password'
            )}
          </button>
        </div>
      </div>

      {/* Account Status */}
      <div className="border-t pt-6">
        <h4 className="text-md font-medium text-gray-800 mb-4">Account Status</h4>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-green-600 mr-2">✓</div>
            <div>
              <p className="text-sm font-medium text-green-800">Account Verified</p>
              <p className="text-sm text-green-700">Your employer account has been verified and is active.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="border-t pt-6">
        <h4 className="text-md font-medium text-red-600 mb-4">Danger Zone</h4>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <FaTrash className="text-red-600 mr-2 mt-0.5" />
            <div className="flex-1">
              <h5 className="text-sm font-medium text-red-800">Delete Account</h5>
              <p className="text-sm text-red-700 mt-1">
                Permanently delete your employer account and all associated data. This action cannot be undone.
              </p>
              <button
                onClick={() => alert('Contact support to delete your account')}
                className="mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'contact', label: 'Contact', icon: FaUser },
    { id: 'notifications', label: 'Notifications', icon: FaBell },
    { id: 'security', label: 'Security', icon: FaLock }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-2">Manage your employer account settings and preferences</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6 pt-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="mr-2" size={16} />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'contact' && renderContactSettings()}
            {activeTab === 'notifications' && renderNotificationSettings()}
            {activeTab === 'security' && renderSecuritySettings()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployerSettings;